/**
 * Inventory (tracker item 30): stock on hand and value of every PRODUCT item,
 * the stock value compared with GL 1300 (the difference is always shown),
 * Receive stock and Adjust count, and a per-item history panel.
 *
 * The list is GET /inventory/valuation (active PRODUCT items plus deleted ones
 * still holding stock), re-read after every change since GL 1300 moves too.
 * Rows at or below their reorder point carry a "Low stock" badge (server
 * `lowStock`). The search (`q`), the "Low stock only" filter (`lowStock=1`)
 * and the open history (`itemId`) live in the query string, so the ledger can
 * link to /inventory?itemId=… The low-stock filter is applied to the loaded
 * list (not the server's ?lowStock=true) because the Receive picker and the
 * history panel need every item. Route-gated to ADMIN /
 * OFFICE_MANAGER (canManageInventory), matching the backend.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createAPIClient, IGLAccountsApi, IInventoryApi } from '../api';
import * as types from '../api/types';
import { inputClass, labelClass, primaryButtonClass, rowActionClass } from '../components/formStyles';
import { ledgerEntryPath } from './ledgerUi';
import { AdjustCountForm, ReceiveStockForm } from './InventoryForms';
import { InventoryHistoryPanel } from './InventoryHistoryPanel';
import {
  changeSummary,
  differenceExplanation,
  filterValuationItems,
  formatQuantity,
  inventoryErrorMessage,
  inventoryMoney,
  inventoryMoneyFromCents,
  LOW_STOCK_PARAM,
  lowStockCount,
  lowStockTitle,
  signedMoney,
  unvaluedNote,
  valuationComparison,
} from './inventoryUi';
import { Toast } from '../components/Toast';

type StockAction = { kind: 'receive' | 'adjust'; itemId: string };

interface SuccessNotice {
  text: string;
  journalEntryId: string | null;
}

export function InventoryPage() {
  const api = useMemo<IInventoryApi & IGLAccountsApi>(() => createAPIClient(), []);
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const historyItemId = params.get('itemId') || '';
  const lowStockOnly = ['1', 'true'].includes(params.get(LOW_STOCK_PARAM) || '');

  const [valuation, setValuation] = useState<types.InventoryValuation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [action, setAction] = useState<StockAction | null>(null);
  const [success, setSuccess] = useState<SuccessNotice | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api.getInventoryValuation()
      .then((result) => { if (active) setValuation(result); })
      .catch((cause) => { if (active) setError(inventoryErrorMessage(cause, 'Could not load the inventory valuation')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, reloadKey]);

  function changeParams(next: Record<string, string>, replace = false) {
    const updated = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => (value ? updated.set(key, value) : updated.delete(key)));
    setParams(updated, { replace });
  }

  function openAction(next: StockAction) {
    setSuccess(null);
    setAction(next);
  }

  function handleDone(kind: StockAction['kind'], result: types.InventoryChangeResponse) {
    setAction(null);
    setSuccess({ text: changeSummary(kind, result), journalEntryId: result.journalEntry?.id ?? null });
    setReloadKey((key) => key + 1);
  }

  const items = valuation?.items ?? [];
  const activeItems = items.filter((item) => !item.deleted);
  const visible = filterValuationItems(items, query, lowStockOnly);
  const lowCount = valuation ? lowStockCount(valuation) : 0;
  const comparison = valuation ? valuationComparison(valuation) : null;
  const note = valuation ? unvaluedNote(valuation.unvaluedItemCount) : null;
  const actionItem = action ? items.find((item) => item.itemId === action.itemId) : undefined;
  const historyItem = items.find((item) => item.itemId === historyItemId);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
        <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
        <button type="button" className={`${primaryButtonClass} w-full sm:w-auto min-h-[44px] sm:min-h-0`} disabled={!valuation || activeItems.length === 0}
          onClick={() => openAction({ kind: 'receive', itemId: '' })}>
          Receive stock
        </button>
      </div>
      <p className="mb-6 max-w-3xl text-sm text-gray-600">
        Stock on hand for product items. Invoices take stock out and cancellations put it back automatically; record
        deliveries with Receive stock and correct counts with Adjust count. Both post to the general ledger.
      </p>

      {success && (
        <Toast kind="success" id={`${success.text}|${success.journalEntryId ?? ''}`}>
          {success.text}
          {success.journalEntryId && (
            <> <Link to={ledgerEntryPath(success.journalEntryId)} className="font-semibold underline">View the journal entry</Link>.</>
          )}
        </Toast>
      )}

      {error && <Toast kind="error">{error}</Toast>}

      {valuation && comparison && (
        <section className="bg-white rounded-lg shadow p-5 sm:p-6 mb-6" aria-label="Valuation">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3 text-sm">
            <div>
              <dt className="text-gray-500">Stock value</dt>
              <dd className="text-xl font-semibold text-gray-900 tabular-nums" data-testid="valuation-total">{inventoryMoney(valuation.totalValue)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">
                {valuation.glAccount ? `GL ${valuation.glAccount.accountNumber} ${valuation.glAccount.accountName}` : 'GL 1300 Inventory - Materials'}
              </dt>
              <dd className="text-xl font-semibold text-gray-900 tabular-nums break-words" data-testid="valuation-gl">
                {comparison.glBalanceCents === null ? 'Account missing' : inventoryMoneyFromCents(comparison.glBalanceCents)}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Difference (stock value − GL)</dt>
              <dd className={`text-xl font-semibold tabular-nums ${comparison.reconciled ? 'text-green-700' : 'text-amber-700'}`} data-testid="valuation-difference">
                {signedMoney(comparison.differenceCents)}
                <span className="ml-2 text-sm font-medium">{comparison.reconciled ? 'Reconciled' : 'Does not reconcile'}</span>
              </dd>
            </div>
          </dl>
          <p className={`mt-4 text-sm ${comparison.reconciled && valuation.glAccount ? 'text-gray-600' : 'text-amber-800'}`} data-testid="difference-explanation">
            {differenceExplanation(valuation)}
          </p>
          {note && <p className="mt-2 text-sm text-amber-800" data-testid="unvalued-note">{note}</p>}
        </section>
      )}

      {action?.kind === 'receive' && (
        <ReceiveStockForm
          key={`receive-${action.itemId}`}
          api={api}
          items={activeItems}
          initialItemId={action.itemId}
          onDone={(result) => handleDone('receive', result)}
          onCancel={() => setAction(null)}
        />
      )}
      {action?.kind === 'adjust' && actionItem && (
        <AdjustCountForm
          key={`adjust-${action.itemId}`}
          api={api}
          item={actionItem}
          onDone={(result) => handleDone('adjust', result)}
          onCancel={() => setAction(null)}
        />
      )}

      {historyItemId && (
        <InventoryHistoryPanel
          key={historyItemId}
          api={api}
          itemId={historyItemId}
          itemName={historyItem?.name}
          reloadKey={reloadKey}
          onClose={() => changeParams({ itemId: '' })}
        />
      )}

      <section className="bg-white rounded-lg shadow p-5 sm:p-6">
        <div className="mb-5 flex flex-wrap items-end gap-x-6 gap-y-3">
          <div className="w-full max-w-sm">
            <label htmlFor="inventory-search" className={labelClass}>Search items</label>
            <input id="inventory-search" type="search" className={inputClass} value={query} placeholder="Item name"
              onChange={(event) => changeParams({ q: event.target.value }, true)} />
          </div>
          <label htmlFor="inventory-low-stock" className="inline-flex items-center gap-2 py-2.5 text-sm text-gray-700">
            <input id="inventory-low-stock" type="checkbox" checked={lowStockOnly}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              onChange={(event) => changeParams({ [LOW_STOCK_PARAM]: event.target.checked ? '1' : '' }, true)} />
            <span className="font-medium">Low stock only</span>
            <span data-testid="low-stock-count"
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${lowCount > 0 ? 'bg-amber-50 text-amber-800 ring-amber-600/20' : 'bg-gray-100 text-gray-700 ring-gray-500/20'}`}>
              {lowCount}
            </span>
          </label>
        </div>

        {loading && !valuation ? <p>Loading inventory…</p> : !valuation ? null : items.length === 0 ? (
          <p className="py-10 text-center text-gray-600">
            No product items yet. Add items of type Product under <Link to="/settings/items" className="text-blue-700 hover:underline">Settings &gt; Items</Link>.
          </p>
        ) : visible.length === 0 ? (
          <p className="py-10 text-center text-gray-600">
            {lowStockOnly && !query.trim()
              ? 'No items are low on stock. Set a reorder point on a product under Settings > Items to get a warning.'
              : lowStockOnly
                ? `No low-stock items match “${query}”.`
                : `No items match “${query}”.`}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
              <caption className="sr-only">Product items</caption>
              <thead className="bg-gray-50">
                <tr>
                  {['Item', 'On hand', 'Unit cost', 'Value', 'Actions'].map((heading) => (
                    <th key={heading} scope="col"
                      className={`px-4 py-3 font-medium text-gray-600 ${['On hand', 'Unit cost', 'Value'].includes(heading) ? 'text-right' : 'text-left'}`}>
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {visible.map((item) => (
                  <tr key={item.itemId} className={item.itemId === historyItemId ? 'bg-blue-50' : undefined}>
                    <td data-label="Item" data-primary className="px-4 py-3 break-words">
                      <span className="font-medium text-gray-900">{item.name}</span>
                      {item.lowStock && (
                        <span title={lowStockTitle(item)} className="ml-2 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20">
                          Low stock
                        </span>
                      )}
                      {item.deleted && (
                        <span title="Deleted item still holding stock" className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-500/20">
                          Deleted
                        </span>
                      )}
                    </td>
                    <td data-label="On hand" className={`px-4 py-3 whitespace-nowrap text-right tabular-nums ${item.quantityOnHand.startsWith('-') ? 'text-red-700' : ''}`}>
                      <span>{formatQuantity(item.quantityOnHand)} <span className="text-gray-500">{item.unitOfMeasure}</span></span>
                    </td>
                    <td data-label="Unit cost" className="px-4 py-3 whitespace-nowrap text-right tabular-nums">{inventoryMoney(item.unitCost)}</td>
                    <td data-label="Value" className="px-4 py-3 whitespace-nowrap text-right tabular-nums">
                      {item.extendedValue === null ? <span className="text-gray-500" title="No unit cost">Not valued</span> : inventoryMoney(item.extendedValue)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-wrap gap-4 [&>button]:min-h-[44px] sm:[&>button]:min-h-0">
                        {!item.deleted && (
                          <>
                            <button type="button" className={rowActionClass} aria-label={`Receive stock for ${item.name}`}
                              onClick={() => openAction({ kind: 'receive', itemId: item.itemId })}>
                              Receive stock
                            </button>
                            <button type="button" className={rowActionClass} aria-label={`Adjust count for ${item.name}`}
                              onClick={() => openAction({ kind: 'adjust', itemId: item.itemId })}>
                              Adjust count
                            </button>
                          </>
                        )}
                        <button type="button" className={rowActionClass} aria-label={`History for ${item.name}`}
                          onClick={() => changeParams({ itemId: item.itemId })}>
                          History
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
